import { useCallback, useEffect, useState } from 'react';
import {
  buildRouteDoc,
  DEFAULT_PROFILE,
  isPostcodeOnly,
  nameRouteEnds,
  placeAt,
  type DraftRoute,
} from '@cad/core';
import { MAPBOX_TOKEN } from './config';
import { RoutesHome } from './components/RoutesHome';
import { MapCanvas } from './components/MapCanvas';
import { Sidebar } from './components/Sidebar';
import { ShareDialog } from './components/ShareDialog';
import { SignInScreen } from './components/SignInScreen';
import { StatsPage } from './components/StatsPage';
import { useRouteEditor } from './editor/useRouteEditor';
import { useRouting } from './useRouting';
import {
  createRoute,
  fetchRoute,
  fetchSession,
  signOut,
  updateRoute,
  type RoutePayload,
  type Session,
} from './api';

/**
 * The gate goes outside everything. Until the session check comes back the app
 * renders nothing at all, so an unauthenticated visitor never gets as far as
 * mounting the map — which would start billing us for tiles.
 */
export function App() {
  /*
    The speed map is the one page in front of the gate: it is shared by link
    with the people who drove the route, who have no account here.
  */
  const stats = new URLSearchParams(window.location.search).get('stats');
  if (stats) {
    const day = Number(new URLSearchParams(window.location.search).get('day'));
    return <StatsPage routeId={stats} initialDay={Number.isFinite(day) && day > 0 ? day : null} />;
  }
  return <Gated />;
}

function Gated() {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    fetchSession()
      .then(setSession)
      .catch(() => setSession({ signedIn: false, configured: true }));
  }, []);

  if (!session) return null;
  if (!session.signedIn) {
    return <SignInScreen configured={session.configured} onSignedIn={setSession} />;
  }

  const signOutAndForget = async () => {
    await signOut().catch(() => undefined);
    setSession({ ...session, signedIn: false });
  };

  /*
    Two pages, told apart by the query rather than the path.

    The path was the obvious choice and does not survive the host: Pages
    serves index.html at "/" and redirects "/index.html" to it, so a rewrite
    of "/plan" to index.html arrives as "/" and the planner looked like the
    home page. The query comes through untouched, and "?r=<id>" is what
    opening a route already used.
  */
  const query = new URLSearchParams(window.location.search);
  if (!query.has('r') && !query.has('new')) {
    return <RoutesHome username={session.username ?? ''} onSignOut={signOutAndForget} />;
  }

  return <Planner session={session} onSignedOut={() => setSession({ ...session, signedIn: false })} />;
}

function Planner({ session, onSignedOut }: { session: Session; onSignedOut: () => void }) {
  const { state, dispatch, canUndo, canRedo, undo, redo, routingKey } = useRouteEditor();
  const routing = useRouting(state.draft, routingKey);

  const [fitRequest, setFitRequest] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Open an existing route via ?r=<id>.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('r');
    if (!id) return;

    let cancelled = false;
    fetchRoute(id)
      .then((route) => {
        if (cancelled) return;
        const draft: DraftRoute = {
          name: route.name,
          notes: route.notes ?? '',
          // As on the phone: re-planned the current way, not the way it was
          // saved. See DEFAULT_PROFILE and draftFromRoute.
          profile: DEFAULT_PROFILE,
          options: route.options,
          waypoints: route.waypoints,
        };
        // Signed in, so anything in the list is ours to edit — no need to check
        // whether this browser happens to hold the edit token.
        dispatch({ type: 'load', draft, id: route.id, revision: route.revision });
        setFitRequest((n) => n + 1);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Could not load that route');
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Keyboard: undo/redo, and delete the selected point.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing = target && /^(INPUT|TEXTAREA)$/.test(target.tagName);

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (!typing && (event.key === 'Delete' || event.key === 'Backspace') && state.selectedWaypointId) {
        event.preventDefault();
        dispatch({ type: 'remove-waypoint', id: state.selectedWaypointId });
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, dispatch, state.selectedWaypointId]);

  // Don't let a closing tab take unsaved work with it.
  useEffect(() => {
    if (!state.dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.dirty]);

  /**
   * Back to a blank route.
   *
   * A navigation rather than a reducer action, for the same reason opening one
   * is: it resets the editor, its history, the map and the URL together. Doing
   * it in place would leave ?r=<id> in the address bar pointing at a route that
   * is no longer on screen, and a reload would quietly bring the old one back.
   */
  const startNewRoute = useCallback(() => {
    window.location.href = '/?new';
  }, []);

  const handleSave = useCallback(async () => {
    if (!routing.result) return;
    setSaving(true);
    setSaveError(null);

    const doc = buildRouteDoc(state.draft, routing.result);

    /*
      Named here rather than on each phone: the answer is the same for
      everyone, so looking it up once when the route is saved costs one
      request instead of one per person per route. Best-effort — a route that
      comes back nameless simply shows its own name, as it did before.
    */
    const first = state.draft.waypoints[0];
    const last = state.draft.waypoints[state.draft.waypoints.length - 1];
    const ends =
      first && last && MAPBOX_TOKEN
        ? await nameRouteEnds(first, last, { accessToken: MAPBOX_TOKEN })
        : { startName: null, endName: null };
    const payload: RoutePayload = {
      name: doc.name,
      ...(doc.notes ? { notes: doc.notes } : {}),
      profile: doc.profile,
      options: doc.options,
      waypoints: doc.waypoints,
      geometry: doc.geometry,
      distance: doc.distance,
      duration: doc.duration,
      legs: doc.legs,
      bbox: doc.bbox,
      // Fetched while routing and stored with the route, so the phone can show
      // limits with no network and no extra quota.
      ...(doc.speedLimits ? { speedLimits: doc.speedLimits } : {}),
      // Fetched while planning and stored with the route, like the limits, so
      // the phone can prompt turns with no network and no extra quota.
      ...(doc.maneuvers ? { maneuvers: doc.maneuvers } : {}),
      ...(ends.startName ? { startName: ends.startName } : {}),
      ...(ends.endName ? { endName: ends.endName } : {}),
    };

    try {
      const saved = state.savedId
        ? await updateRoute(state.savedId, payload)
        : await createRoute(payload);
      dispatch({ type: 'mark-saved', id: saved.id, revision: saved.revision });
      if (!state.savedId) {
        setShareId(saved.id);
        window.history.replaceState(null, '', `/?r=${saved.id}`);
      }
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'Could not save the route');
    } finally {
      setSaving(false);
    }
  }, [routing.result, state.draft, state.savedId, dispatch]);

  return (
    <div className="app">
      <Sidebar
        username={session.username ?? ''}
        onNewRoute={startNewRoute}
        onOpenRoutes={() => {
          window.location.href = '/';
        }}
        onSignOut={async () => {
          await signOut().catch(() => undefined);
          onSignedOut();
        }}
        draft={state.draft}
        route={routing.result}
        routing={routing.loading}
        routingError={routing.error ?? loadError}
        selectedId={state.selectedWaypointId}
        dirty={state.dirty}
        savedId={state.savedId}
        saving={saving}
        saveError={saveError}
        canUndo={canUndo}
        canRedo={canRedo}
        onNameChange={(name) => dispatch({ type: 'set-name', name })}
        onNotesChange={(notes) => dispatch({ type: 'set-notes', notes })}
        onOptionChange={(options) => dispatch({ type: 'set-options', options })}
        onSelect={(id) => dispatch({ type: 'select-waypoint', id })}
        onRemove={(id) => dispatch({ type: 'remove-waypoint', id })}
        onToggleKind={(id, kind) => dispatch({ type: 'set-waypoint-kind', id, kind })}
        onRename={(id, name) => dispatch({ type: 'rename-waypoint', id, name })}
        onReorder={(from, to) => dispatch({ type: 'reorder-waypoints', from, to })}
        onReverse={() => dispatch({ type: 'reverse' })}
        onClear={() => dispatch({ type: 'clear' })}
        onUndo={undo}
        onRedo={redo}
        onSave={handleSave}
        onFit={() => setFitRequest((n) => n + 1)}
      />

      <MapCanvas
        waypoints={state.draft.waypoints}
        route={routing.result}
        selectedWaypointId={state.selectedWaypointId}
        fitRequest={fitRequest}
        onMapClick={(lng, lat) => dispatch({ type: 'add-waypoint', lng, lat })}
        onInsertWaypoint={(index, lng, lat) => dispatch({ type: 'add-waypoint', lng, lat, index })}
        onWaypointDrag={(id, lng, lat, commit) => {
          dispatch({ type: 'move-waypoint', id, lng, lat, commit });
          /*
            A stop named only by its postcode has just been put somewhere on
            purpose — that is the point of dragging it — so it takes the name
            of where it landed, as a pin placed on the phone does. Anything
            the planner has typed as a name is theirs and is left alone.
          */
          const moved = state.draft.waypoints.find((w) => w.id === id);
          if (commit && moved?.kind === 'stop' && isPostcodeOnly(moved.name) && MAPBOX_TOKEN) {
            void placeAt([lng, lat], { accessToken: MAPBOX_TOKEN }).then((place) => {
              if (place?.name) dispatch({ type: 'rename-waypoint', id, name: place.name });
            });
          }
        }}
        onWaypointSelect={(id) => dispatch({ type: 'select-waypoint', id })}
      />

      {shareId && <ShareDialog routeId={shareId} onClose={() => setShareId(null)} />}
    </div>
  );
}
